import type { ButtonPriority, ButtonSize } from '@timelyapp/react';
import { Button, ButtonGroup } from '@timelyapp/react';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import type { Props as PopoverDropdownListProps } from 'components/popovers/PopoverDropdownList';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import React, { useCallback, useMemo } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Merge } from 'types/utility';
import { BorderlessStar } from './Icons';

export type Option<T = string | number> = DropdownOption & {
  value: T;
  'data-testid'?: string;
};

interface OwnProps<T extends Option<V>, V> {
  options: Array<T>;
  /** Collapse the options, showing only target + dropdown instead of ButtonGroup  */
  isCollapsed: boolean;
  dropdownTarget?: React.ReactElement;
  buttonPriority: ButtonPriority;
  size: ButtonSize;
  caret: boolean;
  onClick: (option: T, event: React.SyntheticEvent) => unknown;
  testIdScope: string;
}

type Props<T extends Option<V>, V> = Merge<PopoverDropdownListProps<T>, OwnProps<T, V>>;

/**
 * CollapsibleOptionPicker returns a ButtonGroup or PopoverDropdownList
 * based on `isCollapsed`. Useful for shrinking ButtonGroups when they don't fit/small viewports.
 */
function CollapsibleOptionPicker<T extends Option<V>, V extends string | number | boolean | null>(
  props: Props<T, V>
) {
  const {
    options,
    isCollapsed,
    testIdScope,
    buttonPriority,
    size,
    caret,
    dropdownTarget,
    className,
    onClick,
    ...rest
  } = props;

  const activeOption = useMemo(() => options.find(o => o.active), [options]);
  const massagedOptions = useMemo(() => {
    return options.map(o => ({
      ...o,
      label: o.name ?? '',
      icon: o.isLockedFeature ? <BorderlessStar /> : undefined,
    }));
  }, [options]);
  const handleClickOption = useCallback(
    (value: V, e: React.SyntheticEvent) => {
      const option = options.find(o => o.value === value);
      if (option) {
        onClick(option, e);
      }
    },
    [options, onClick]
  );

  // If collapsed, show target /w dropdowns
  if (isCollapsed && options.length > 1) {
    const target = dropdownTarget || (
      <Button caret={caret} priority={buttonPriority} size={size}>
        {!!activeOption && !!activeOption.faIcon && <FontAwesome name={activeOption.faIcon} />}
        <span>{!!activeOption && activeOption.name}</span>
      </Button>
    );
    return <PopoverDropdownList options={options} {...rest} onClick={onClick} target={target} />;
  }

  // Non-collapsed shows as ButtonGroup
  return (
    <ButtonGroup
      options={massagedOptions}
      activeValue={activeOption!.value}
      className={className}
      onChange={handleClickOption}
      size={size === 'small' || size == null ? 'small' : 'medium'}
    />
  );
}
CollapsibleOptionPicker.defaultProps = {
  size: 'medium',
  buttonPriority: 'medium',
  isCollapsed: false,
  caret: true,
  testIdScope: '',
};
export default CollapsibleOptionPicker;
